import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { formatDateTime } from './optionsHistoryUtils';
import {
  buildSpotRebalanceSummary,
  formatActivityAmount,
  formatActivityFee,
  formatMarketSideLabel,
  getInstrumentTypeLabel,
  getPortfolioRoleBadgeMeta,
  getPortfolioRoleLabel,
} from './optionsPortfolioActivityUtils';

export const OptionsPortfolioActivityCard = ({ item, index = 0 }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const roleBadge = useMemo(() => getPortfolioRoleBadgeMeta(item?.effective_role), [item]);
  const roleLabel = useMemo(() => getPortfolioRoleLabel(item?.effective_role, t), [item, t]);
  const instrumentTypeLabel = useMemo(() => getInstrumentTypeLabel(item?.instrument_type, t), [item, t]);
  const timestampLabel = useMemo(() => formatDateTime(item?.event_at, isPortuguese ? 'pt' : 'en'), [item, isPortuguese]);
  const isSpot = String(item?.instrument_type).toUpperCase() === 'SPOT';
  const sideLabel = useMemo(() => formatMarketSideLabel(item?.market_side), [item]);
  const rebalance = useMemo(() => (isSpot ? buildSpotRebalanceSummary(item) : null), [isSpot, item]);
  const feesLabel = formatActivityFee(item?.commission_native, isSpot ? rebalance?.feeCurrency : item?.settlement_currency);

  return (
    <div className="rounded-[22px] border border-white/10 bg-white/[0.03] px-4 py-4 sm:px-5" data-testid={`options-portfolio-activity-card-${index}`}>
      <div className="flex flex-wrap items-center gap-2" data-testid={`options-portfolio-activity-badges-${index}`}>
        <Badge className={roleBadge.className} data-testid={`options-portfolio-activity-role-${index}`}>{roleLabel}</Badge>
        <Badge className="border-white/10 bg-white/[0.05] text-white/75" data-testid={`options-portfolio-activity-instrument-type-${index}`}>{instrumentTypeLabel}</Badge>
      </div>

      {isSpot ? (
        <>
          <div className="mt-3 flex items-center gap-2">
            <OptionsAssetIcon assetKey={rebalance?.fromCurrency} size={16} />
            <p className="text-lg font-black text-white" data-testid={`options-portfolio-activity-title-${index}`}>
              {rebalance?.fromCurrency || '—'} <span className="text-white/40">&rarr;</span> {rebalance?.toCurrency || '—'}
            </p>
          </div>
          <div className="mt-3 grid gap-x-5 gap-y-3 border-y border-white/10 py-3 sm:grid-cols-4" data-testid={`options-portfolio-activity-metrics-${index}`}>
            <CompactMetric label={t('history.sold')} value={formatActivityAmount(rebalance?.soldAmount, rebalance?.soldCurrency)} testId={`options-portfolio-activity-sold-${index}`} />
            <CompactMetric label={t('history.received')} value={formatActivityAmount(rebalance?.receivedAmount, rebalance?.receivedCurrency)} testId={`options-portfolio-activity-received-${index}`} />
            <CompactMetric label={t('dashboard.fees')} value={feesLabel} testId={`options-portfolio-activity-fees-${index}`} />
            <CompactMetric label={t('history.activityDate')} value={timestampLabel} testId={`options-portfolio-activity-date-${index}`} />
          </div>
        </>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2">
            <OptionsAssetIcon assetKey={item?.underlying || item?.base_currency} size={16} />
            <p className="text-lg font-black text-white" data-testid={`options-portfolio-activity-title-${index}`}>{item?.instrument_name || '—'}</p>
            {sideLabel ? <span className="text-sm font-semibold text-white/65" data-testid={`options-portfolio-activity-side-${index}`}>{sideLabel}</span> : null}
          </div>
          <div className="mt-3 grid gap-x-5 gap-y-3 border-y border-white/10 py-3 sm:grid-cols-4" data-testid={`options-portfolio-activity-metrics-${index}`}>
            <CompactMetric label={t('history.opened')} value={timestampLabel} testId={`options-portfolio-activity-opened-${index}`} />
            <CompactMetric label={t('positions.size')} value={formatActivityAmount(item?.amount_raw, item?.quote_currency || item?.underlying)} testId={`options-portfolio-activity-size-${index}`} />
            <CompactMetric label={t('history.entryPrice')} value={formatActivityAmount(item?.average_price, item?.quote_currency || 'USD')} testId={`options-portfolio-activity-price-${index}`} />
            <CompactMetric label={t('dashboard.fees')} value={feesLabel} testId={`options-portfolio-activity-fees-${index}`} />
          </div>
        </>
      )}
    </div>
  );
};

const CompactMetric = ({ label, value, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-testid={`${testId}-label`}>{label}</p>
    <p className="mt-1 break-words text-sm font-semibold text-white tabular-nums" data-testid={`${testId}-value`}>{value}</p>
  </div>
);

export default OptionsPortfolioActivityCard;